/**
 * Custom user message renderer.
 *
 * Replaces the library's default UserMessage so that:
 * 1. The action bar (edit + copy) sits BELOW the bubble on its own row,
 *    right-aligned, matching the layout of the assistant message action bar.
 *    The library default renders just an Edit button on the left middle of
 *    the bubble, which is visually disconnected from the text.
 * 2. A Copy button is added alongside Edit so users can reuse their own
 *    message text without retyping.
 *
 * Layout notes:
 * - A fixed-height spacer div always occupies row 3 so the conversation
 *   layout doesn't jump when the action bar mounts/unmounts.
 * - `MessagePrimitive.If lastOrHover` is the same condition the library
 *   uses: last message always shows, non-last only shows while hovering.
 *   This keeps the action bar perfectly in sync with the message's
 *   `isHovering` state so it can't get stuck visible.
 * - `agent-user-action-bar` class wires the text color to
 *   `--aui-muted-foreground` (see `src/index.css`) so our ghost-icon
 *   buttons match the assistant action bar's tone exactly.
 */

import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { MessagePrimitive, ActionBarPrimitive, useAuiState } from '@assistant-ui/react';
import { UserMessage, BranchPicker } from '@assistant-ui/react-ui';
import { CopyIcon, PencilIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { MentionAwareUserText } from './mention-aware-user-text';
import { StructuredAnswerBlock } from './StructuredAnswerBlock';
import { SystemEventChip } from './system-event-chip';
import type { AskUserRef } from '@/types/inbox';

const MAX_HEIGHT = 160; // px (~6-8 lines of text)

export function AgentUserMessage() {
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const messageMetadata = useAuiState((s) => s.message.metadata);
  const parts = useAuiState((s) => s.message.parts);

  const checkOverflow = useCallback(() => {
    const el = contentRef.current;
    if (el) setIsOverflowing(el.scrollHeight > MAX_HEIGHT);
  }, []);

  useLayoutEffect(() => {
    checkOverflow();
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkOverflow]);

  // Platform-injected turns (credential-continuation nudges etc.) collapse to
  // a system-event chip instead of a user bubble. Keyed on the message
  // `source` attribute (threaded through live injection AND history restore),
  // with a SYSTEM NOTE text-prefix fallback for turns persisted before the
  // backend stamped `source` on this path.
  const custom = messageMetadata?.custom as
    | { askUserRef?: AskUserRef; source?: string }
    | undefined;
  const firstText =
    (parts?.find((p) => p.type === 'text') as { text?: string } | undefined)?.text ?? '';
  const isSystemEvent =
    custom?.source === 'event_notification' || /^SYSTEM NOTE:/.test(firstText);
  if (isSystemEvent) {
    return (
      <UserMessage.Root>
        <SystemEventChip text={firstText} />
        <BranchPicker />
      </UserMessage.Root>
    );
  }

  // A sent ask_user answer renders as a structured "Answered" block instead of
  // plain text, driven by the message's `askUserRef` snapshot (live and reload).
  const askUserRef = custom?.askUserRef;
  if (askUserRef?.kind === 'answer') {
    const { snapshot } = askUserRef;
    return (
      <UserMessage.Root>
        <StructuredAnswerBlock
          questions={snapshot.questions}
          answers={'answers' in snapshot ? snapshot.answers : []}
        />
        <BranchPicker />
      </UserMessage.Root>
    );
  }

  return (
    <UserMessage.Root>
      <UserMessage.Attachments />
      <MessagePrimitive.If hasContent>
        <div className="relative col-start-2">
          <div
            ref={contentRef}
            className={!expanded && isOverflowing ? 'overflow-hidden' : undefined}
            style={!expanded && isOverflowing ? { maxHeight: MAX_HEIGHT } : undefined}
          >
            <UserMessage.Content components={{ Text: MentionAwareUserText }} />
          </div>
          {!expanded && isOverflowing && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[hsl(var(--aui-muted))] to-transparent" />
          )}
        </div>
        {isOverflowing && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="col-start-2 mt-1 flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
          >
            {expanded ? (
              <>
                <ChevronUpIcon className="h-3 w-3" /> Show less
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-3 w-3" /> Show more
              </>
            )}
          </button>
        )}
        <div className="agent-user-action-bar col-start-1 col-end-[-1] row-start-3 mt-1.5 flex h-6 items-center justify-end">
          <MessagePrimitive.If lastOrHover>
            <ActionBarPrimitive.Root
              hideWhenRunning
              className="flex items-center gap-0.5"
            >
              <ActionBarPrimitive.Copy asChild>
                <button
                  type="button"
                  title="Copy"
                  aria-label="Copy"
                  className="aui-button aui-button-ghost aui-button-icon"
                >
                  <MessagePrimitive.If copied>
                    <CheckIcon />
                  </MessagePrimitive.If>
                  <MessagePrimitive.If copied={false}>
                    <CopyIcon />
                  </MessagePrimitive.If>
                </button>
              </ActionBarPrimitive.Copy>
              <ActionBarPrimitive.Edit asChild>
                <button
                  type="button"
                  title="Edit"
                  aria-label="Edit"
                  className="aui-button aui-button-ghost aui-button-icon"
                >
                  <PencilIcon />
                </button>
              </ActionBarPrimitive.Edit>
            </ActionBarPrimitive.Root>
          </MessagePrimitive.If>
        </div>
      </MessagePrimitive.If>
      <BranchPicker />
    </UserMessage.Root>
  );
}
